import { BillingDetails, PaymentTransactionHistory } from '../../services/payment.service';
import { ReceiptCard, resolvePaymentReceiptCard } from './card-payment-label.util';

export interface PaymentInfo {
  transaction: PaymentTransactionHistory | null;
  card: ReceiptCard | null;
  billing: BillingDetails | null;
}

const NOT_CHARGED_STATUSES = new Set(['failed', 'canceled']);

// A card-on-file event (`payment_method_added`) is a $0 history row with a made-up `pmadd_`
// intent id, not a charge.
function isCharge(transaction: PaymentTransactionHistory): boolean {
  return (
    !transaction.payload?.event &&
    Boolean(String(transaction.payment_intent_id ?? '').trim()) &&
    !NOT_CHARGED_STATUSES.has(String(transaction.payment_status ?? '').toLowerCase())
  );
}

function paidAt(transaction: PaymentTransactionHistory): number {
  const time = new Date(transaction.payment_date).getTime();
  return Number.isNaN(time) ? Number.NEGATIVE_INFINITY : time;
}

/**
 * What an order's Payment Info box describes: its latest charge, with the card and billing that
 * charge was made with. A membership renews as further payments on its signup order, on
 * whatever card is on file then, so after the card is updated only the latest charge carries
 * the new one. The order-level card and billing are read from the first payment and are only
 * the fallback for a payment stored without its own. An order paid once reads as before.
 */
export function resolvePaymentInfo(
  transactions: PaymentTransactionHistory[],
  orderCard: ReceiptCard | null | undefined,
  orderBilling: BillingDetails | null | undefined
): PaymentInfo {
  let transaction: PaymentTransactionHistory | null = null;
  for (const candidate of transactions.filter(isCharge)) {
    if (!transaction || paidAt(candidate) >= paidAt(transaction)) {
      transaction = candidate;
    }
  }
  transaction = transaction ?? transactions[0] ?? null;
  if (!transaction) {
    return { transaction: null, card: orderCard?.last4 ? orderCard : null, billing: null };
  }

  // A payment stored with its own card also has its own billing (null when the charge had none).
  const payload = transaction.payload;
  const ownBilling = Boolean(payload?.last4) && payload?.billing_details !== undefined;
  return {
    transaction,
    card: resolvePaymentReceiptCard(transaction, orderCard),
    billing: ownBilling ? (payload?.billing_details ?? null) : (orderBilling ?? null),
  };
}
